import { t } from 'i18next';
import * as yup from 'yup';

import { emailRegex, phoneSchema } from '~constants/regexConst';
import { RoleItem } from '~hooks/useUsers/models';

export const userEmailSchema = yup
  .string()
  .email(`${t('form_validation_errors.required', { field: t('form_fields.email') })}`)
  .matches(emailRegex, `${t('form_validation_errors.invalid_email')}`);

export const userPhoneSchema = phoneSchema.required(
  `${t('form_validation_errors.required', { field: t('form_fields.phone') })}`,
);

const companySharesSchema = yup
  .array()
  .of(
    yup.object().shape({
      id: yup.string(),
      receiverCompany: yup.object().shape({ id: yup.string(), legalName: yup.string() }),
      rateOverride: yup.boolean().notRequired(),
    }),
  )
  .notRequired();

const userFormValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required(
      `${t('form_validation_errors.required', { field: t('form_fields.first_name') })}`,
    ),
  lastName: yup
    .string()
    .required(
      `${t('form_validation_errors.required', { field: t('form_fields.last_name') })}`,
    ),
  company: yup
    .object()
    .typeError(
      `${t('form_validation_errors.required', { field: t('form_fields.company') })}`,
    )
    .required(
      `${t('form_validation_errors.required', { field: t('form_fields.company') })}`,
    ),
  email: userEmailSchema,
  userRoles: yup
    .array()
    .test(
      'at-least-one-role-or-billing-portal-access',
      `${t('form_validation_errors.min_count', {
        field: t('form_fields.role'),
        count: 1,
      })}`,
      function (value) {
        return (
          (value?.length ?? 0) >= 1 || Boolean(this.parent.billingPortalAccessFieldValue)
        );
      },
    )
    .typeError(
      `${t('form_validation_errors.required', { field: t('form_fields.role') })}`,
    )
    .required(
      `${t('form_validation_errors.required', { field: t('form_fields.role') })}`,
    ),
  phone: userPhoneSchema,
  timeZone: yup
    .mixed()
    .test(
      'required',
      `${t('form_validation_errors.required', { field: t('form_fields.time_zone') })}`,
      (value) => {
        if (typeof value === 'string') return value.length > 0;
        if (value && typeof value === 'object' && 'value' in value)
          return !!(value as { value: string }).value;
        return false;
      },
    ),
  companyShares: companySharesSchema,
  textToAcceptFieldEnabled: yup.boolean(),
  textToAcceptFieldValue: yup.boolean(),
  billingPortalAccessFieldEnabled: yup.boolean(),
  billingPortalAccessFieldValue: yup.boolean(),
  hideRatesFieldEnabled: yup.boolean(),
  hideRatesFieldValue: yup.boolean(),
  noLocationAlertFieldEnabled: yup.boolean(),
  noLocationAlertFieldValue: yup.boolean(),
  sites: yup
    .array()
    .required()
    .when('userRoles', {
      is: (userRoles: RoleItem[] | null) => {
        return userRoles?.some((roleItem) => {
          // Temporary fix for UserForm v2
          if ('label' in roleItem && typeof roleItem.label === 'string') {
            return roleItem.label.toLowerCase() === 'viewer';
          }

          return roleItem.name.toLowerCase() === 'viewer';
        });
      },
      then: (schema) => schema.min(0),
      otherwise: (schema) => schema.min(0),
    }),
  labels: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        color: yup.string(),
      }),
    )
    .max(5)
    .notRequired(),
  accountingId: yup.string().nullable().notRequired(),
});

const equipmentObjectSchema = yup
  .object()
  .shape({
    name: yup.string().notRequired(),
    id: yup.string().notRequired(),
  })
  .nullable()
  .notRequired();

export const driverFormValidationSchema = yup.object().shape({
  companyShares: companySharesSchema,
  additionalEquipment: equipmentObjectSchema,
  defaultDriverAdditionalEquipment: equipmentObjectSchema,
  intrastateHoursOfService: yup.boolean(),
  labels: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        color: yup.string(),
      }),
    )
    .max(5)
    .notRequired(),
});

export const userWithRequiredEmailValidationSchema = userFormValidationSchema
  .clone()
  .shape({
    email: userEmailSchema.required(
      `${t('form_validation_errors.required', { field: t('form_fields.email') })}`,
    ),
    phone: userPhoneSchema.notRequired(),
  });

export const userWithRequiredEmailAndOmitedPhoneValidationSchema =
  userWithRequiredEmailValidationSchema.clone().omit(['phone']);

// OTP_SMS enabled -> either email or phone required
export const userWithEmailOrPhoneValidationSchema = userFormValidationSchema
  .clone()
  .shape({
    email: userEmailSchema
      .transform((val) => {
        if (!val?.length) {
          return undefined;
        }

        return val;
      })
      .notRequired()
      .when('phone', {
        is: (phone: string | undefined) => {
          return Boolean(!phone || phone.length === 0);
        },
        then: () => {
          return userEmailSchema.required(
            `${t('form_validation_errors.either_one_or_other', { one: t('form_fields.email'), other: t('form_fields.phone') })}`,
          );
        },
      }),
    phone: userPhoneSchema
      .transform((val) => {
        if (!val?.length) {
          return undefined;
        }

        return val;
      })
      .notRequired(),
  });

export const userWithEmailAndOmitedPhoneValidationSchema =
  userWithEmailOrPhoneValidationSchema.clone().omit(['phone']);
